import { HeaderComponent } from "@/components/headerComponet";
import style from "./kanban.module.css";
import { ActiveItemProvider } from "../home/context/ActiveItemContext";
import DrawerComponent from "@/components/DrawerComponent";
import { useState } from "react";
import { Toolbar } from "@mui/material";
import { KanbanSearchBarPage } from "./KanbanSearchComponent";
import { KanbanColumnComponent } from "./KanbanColumn";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { LeadProvider, useLeads } from "../leads/context/leadContext";
import { CustomSnackbar } from "@/utils/alert";
import attendanceApi from "@/services/api/attendance";
import { KanbanSearchProvider } from "./kanbanSearchContext/KanbanSearchContext";

type Severity = "success" | "error";
dayjs.extend(isToday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export function KanbanPageComponent() {
  const [showAlert, setShowAlert] = useState(false);
  const [selectedOptionCreatedAt, setSelectedOptionCreatedAt] = useState<string | null>(null);
  const [selectedOptionAttendant, setSelectedOptionAttendant] = useState<string[]>([]);
  const [selectedOptionJourneyStage, setSelectedOptionJourneyStage] = useState<string[]>([]);
  const [selectedOptionOrigin, setSelectedOptionOrigin] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [reload, setReload] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    severity: "success" as Severity,
    message: "",
  });

  interface Column {
    id : string;
    title : string;
    stage : string;
    titleColor : string;
    headerColor : string;
    atendancesListSection : any[];
  }

  const initialColumns : Column[] = [
    { id: "column-1", title: "Não classificado", stage: "NAO_CLASSIFICADO", titleColor: "#B92706", headerColor: "#F4AE57", atendancesListSection: []},
    { id: "column-2", title: "Perdido", stage: "PERDIDO", titleColor: "#B92706", headerColor: "#F4AE57", atendancesListSection: []},
    { id: "column-3", title: "Em contato", stage: "EM_CONTATO", titleColor: "#B92706", headerColor: "#F4AE57", atendancesListSection: []},
    { id: "column-4", title: "Briefing", stage: "BRIEFING", titleColor: "#fff", headerColor: "#E97156", atendancesListSection: []},
    { id: "column-5", title: "Em negociação", stage: "NEGOCIACAO", titleColor:"#fff", headerColor: "#E97156", atendancesListSection: []},
    { id: "column-7", title: "Conferência de medidas", stage: "CONFERENCIA_MEDIDAS", titleColor:"#fff", headerColor: "#E97156", atendancesListSection: []},
    { id: "column-8", title: "Projeto técnico", stage: "PROJETO_TECNICO", titleColor:"#fff", headerColor: "#E97156", atendancesListSection: []},
    { id: "column-9", title: "Em produção/entrega", stage: "PRODUCAO", titleColor:"#fff", headerColor: "#E97156", atendancesListSection: []},
    { id: "column-10", title: "Montagem", stage: "AGENDAR_MONTAGEM", titleColor:"#fff", headerColor: "#E97156", atendancesListSection: []},
    { id: "column-12", title: "Obter feedback", stage: "FEEDBACK", titleColor:"#fff", headerColor: "#E97156", atendancesListSection: []},
    { id: "column-13", title: "Venda Concluída", stage: "POS_VENDA", titleColor:"#fff", headerColor: "#E97156", atendancesListSection: []}
  ];  
  const [columns, setColumns] = useState(initialColumns);
  const [carregando, setCarregando] = useState(true);

  const leadContext = useLeads();

  function filterByDate(data: any) {
    if (!selectedOptionCreatedAt && !startDate && !endDate) return data;

    const filteredData: any = {};

    Object.keys(data).forEach((stage) => {
      filteredData[stage] = data[stage].filter((item: any) => {
        const createdDate = dayjs(item.criadoEm);

        if (selectedOptionCreatedAt === "Hoje") {
          return createdDate.isToday();
        } else if (selectedOptionCreatedAt === "Esta semana") {
          const startOfWeek = dayjs().startOf('week');
          const endOfWeek = dayjs().endOf('week');
          return createdDate.isSameOrAfter(startOfWeek) && createdDate.isSameOrBefore(endOfWeek);
        } else if (selectedOptionCreatedAt === "Este mês") {
          const startOfMonth = dayjs().startOf('month');
          const endOfMonth = dayjs().endOf('month');
          return createdDate.isSameOrAfter(startOfMonth) && createdDate.isSameOrBefore(endOfMonth);
        }

        if (startDate && endDate) {

          return createdDate.isSameOrAfter(dayjs(startDate)) && createdDate.isSameOrBefore(dayjs(endDate));
        }

        return true;
      });
    });

    return filteredData;
  }


  function filterByAtendant(data: any) {
    if (selectedOptionAttendant.length === 0) return data;

    const filteredData: any = {};

    Object.keys(data).forEach((stage) => {
      filteredData[stage] = data[stage].filter((item: any) => {
        const matches = selectedOptionAttendant.includes(item.atendenteId);
        return matches; // Certifique-se de que a comparação retorna true ou false
      });
    });

    return filteredData;
  }

  function filterByOrigem(data: any) {
    if (selectedOptionOrigin.length === 0) return data; // Se não houver seleção, retorna todos os dados

    const filteredOrigin: any = {};

    Object.keys(data).forEach((stage) => {
      filteredOrigin[stage] = data[stage].filter((item: any) => {
        // Verifica se alguma das opções selecionadas está presente na origem do item
        return selectedOptionOrigin.some((origin) => {
          const matches = origin.toLowerCase() === item.origem?.toLowerCase();
          return matches; // Certifique-se de que a comparação retorna true ou false
        });
      });
    });

    return filteredOrigin;
  }



  function filterBySearch(data: any) {
    if (!searchTerm) return data;

    const filteredSearch: any = {};

    Object.keys(data).forEach((stage) => {
      filteredSearch[stage] = data[stage].filter((item: any) => {
        return (
          item.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) || 
          item.telefone?.includes(searchTerm) ||
          item.estagio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.origem?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    });

    return filteredSearch;
  }

  function handleAlertClose() {
    setShowAlert(false);
  }
  
  return (
    <div className={style.kanbanPage}>
      <KanbanSearchProvider>

        <HeaderComponent />
        <Toolbar />
        <ActiveItemProvider>
          <DrawerComponent />
        </ActiveItemProvider>
        <CustomSnackbar
          open={showAlert}
          severity={alertInfo.severity}
          message={alertInfo.message}
          handleClose={handleAlertClose}
        />
        <div className={style.sectionBodyKanban}>
          <h3 className={`title-table ${style.titleTable}`}>Kanban</h3>
          <div className={style.kanbanSearch}>
            <KanbanSearchBarPage
              selectedOptionJourneyStage={selectedOptionJourneyStage}
              setSelectedOptionJourneyStage={setSelectedOptionJourneyStage}
              reload={reload}
              setReload={setReload}
              selectedOptionOrigin={selectedOptionOrigin}
              setSelectedOptionOrigin={setSelectedOptionOrigin}
              startDate={startDate}  // Adicione isso
              setStartDate={setStartDate}  // Adicione isso
              endDate={endDate}  // Adicione isso
              setEndDate={setEndDate}  // Adicione isso
            />
          </div>
          <div className={style.kanbanBody}>
            {columns.map((column) => (
              <div key={column.id} className={style.section}>
                <KanbanColumnComponent
                  stage={column.stage}
                  title={column.title}
                  headerColor={column.headerColor}
                  titleColor={column.titleColor}
                  amount={column.atendancesListSection?.length}
                  key={leadContext.reloadList ? "reload" : "no-reload"}
                  // lastElementRef={lastElementRefAtendimento}
                  // loadAttendances={getAtendimentos}
                  // loadLeads={loadMoreLeads}
                />
              </div>
            ))}
          </div>
        </div>
      </KanbanSearchProvider>
    </div>
  );
}

export function KanbanPage() {
  return (
    <LeadProvider>
      <KanbanPageComponent />
    </LeadProvider>
  );
}
